"use client";

import { useCallback, useEffect, useState } from "react";
import KwikPassScript from "./KwikPassScript";
import { usePathname } from "next/navigation";
import { useAuth } from "@/contexts/auth-context";
import { dispatchOtherPageViewEvent } from "./KwikpassEvent";
import { CartStorage } from "@/libs/cart-storage";

// Extend Window interface to include KwikPass event handlers
declare global {
  interface Window {
    handleCustomLogin?: (param: boolean) => void;
  }
}

// Define custom event types for KwikPass
type KwikPassLoginEvent = CustomEvent<{
  [key: string]: unknown;
}>;

// Extend WindowEventMap to include KwikPass events
declare global {
  interface WindowEventMap {
    "user-loggedin": KwikPassLoginEvent;
    user_loggedin_merchant: KwikPassLoginEvent;
  }
}

export default function KwikPass() {
  const pathname = usePathname();
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const { login } = useAuth();

  // Callback to set scriptLoaded state to true when the script has loaded
  const onLoaded = useCallback(() => {
    setScriptLoaded(true);
  }, []);

  // Handler for KwikPass login (this is triggered when a user logs in via SSO or KwikPass)
  const handleKwikpassLogin = useCallback(
    async (event: KwikPassLoginEvent) => {
      // Handle the login after the user is authenticated via SSO or KwikPass.
      console.log("User logged in via KwikPass:", event);

      // Store user data in auth context
      if (event.detail && Object.keys(event.detail).length > 0) {
        login(event.detail);
      }
    },
    [login]
  );

  // Handler for user login (this is triggered when a user logs into the website)
  const handleUserLogin = useCallback(
    async (event: KwikPassLoginEvent) => {
      // Inside this function, you can handle user login or session login.
      console.log("User logged in to merchant site:", event);

      // Store user data in auth context
      if (event.detail && Object.keys(event.detail).length > 0) {
        login(event.detail);
      }
    },
    [login]
  );

  // Effect hook to add event listeners once the script is loaded
  useEffect(() => {
    if (scriptLoaded && typeof window !== "undefined") {
      // Add event listeners for user and merchant login events
      window.addEventListener("user-loggedin", handleKwikpassLogin); // SSO or KwikPass login event
      window.addEventListener("user_loggedin_merchant", handleUserLogin); // User login event

      // Cleanup function to remove event listeners on component unmount
      return () => {
        window.removeEventListener("user-loggedin", handleKwikpassLogin);
        window.removeEventListener("user_loggedin_merchant", handleUserLogin);
      };
    }
  }, [scriptLoaded, handleKwikpassLogin, handleUserLogin]);

  // Effect hook to send page view event whenever the page changes
  useEffect(() => {
    if (scriptLoaded && typeof window !== "undefined") {
      // Send page view event (you can expand this functionality as needed)
      console.log("Page view:", pathname);
      if (!pathname.startsWith("/products")) {
        dispatchOtherPageViewEvent({
          cart_id: CartStorage.getCartToken() || "",
        });
      }
    }
  }, [scriptLoaded, pathname]);

  return <KwikPassScript onLoaded={onLoaded} />;
}
